import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Builder } from 'builder-pattern';
import jwtDecode from 'jwt-decode';
import { BehaviorSubject } from 'rxjs';
import { MenuService } from 'src/app/components/menu/service/menu.service';
import { TokenContent, TokenData } from 'src/app/shared/interfaces/token-data';
import { AppLoaderService } from 'src/app/shared/services/app-loader.service';
import { HttpClient } from "@angular/common/http";

import { TokenValidationService } from './token-validation/token-validation.service';
import { resetOAuthAttemptCount } from './pkce-storage.util';

@Injectable({
  providedIn:'root'
})
export class LoggedInService {
  valueFromServer: any;

  constructor(
    private readonly tokenValidationService: TokenValidationService,
    private readonly menuService: MenuService,
    private readonly loaderService: AppLoaderService,
    private readonly router: Router,
    private readonly http: HttpClient
  ){}

  private tokenData$: BehaviorSubject<TokenData> = new BehaviorSubject<TokenData>({} as TokenData);
  tokenContent$: BehaviorSubject<TokenContent> = new BehaviorSubject<TokenContent>({} as TokenContent);

  set tokenLocalStorage(token: any) {
    if(token) {
      sessionStorage.setItem("tokenUserData", token);
    }
  }

  get tokenLocalStorage(): any {
    return sessionStorage.getItem("tokenUserData");
  }

  set tokenData(token: TokenData) {
    if(!!token) {
      this.tokenData$.next(token);
    }
  }

  get tokenData(): TokenData {
    return this.tokenData$.getValue()
  }

  getTokenFromUrl(): any {
    const code = new URLSearchParams(window.location.search).get('code');
    if (code) {
      sessionStorage.setItem("code", code);
    }
    return code;
  }

  handleTokenExpiration(token: any): boolean {
    const expiry = (JSON.parse(atob(token.split('.')[1]))).exp;
    return (Math.floor((new Date).getTime() / 1000)) >= expiry;
  }

  tokenIsNullOrEmpty(token: any): boolean {
    return token === null || token === undefined || token === '';
  }

  redirectToPing(): void {
    resetOAuthAttemptCount();
    this.loaderService.showLoader('dark');
    sessionStorage.clear();
    this.tokenValidationService.pingRedirect();
  }

  redirectlogout(): void {
    this.tokenValidationService.logoutAndRedirectToLogin();
  }

  canActivate(perfil: number): boolean {
    if(this.tokenData?.functions){
      const funcao = this.tokenData.functions.find(
        funcao => funcao.functionId === perfil
      )
      return funcao ? true : false;
    } else {
      return false;
    }
  }

  syncTokenContentFromSession(): void {
    const accessToken = sessionStorage.getItem('PING_ACCESS_TOKEN');
    const idToken = sessionStorage.getItem('PING_ID_TOKEN');

    if (!accessToken && !idToken) {
      return;
    }

    this.tokenContent$.next(this.buildTokenContent(accessToken, idToken));
  }

  isInternalUser(): boolean {
    this.syncTokenContentFromSession();
    return this.parseBooleanClaim(this.getInternalUserClaimValue());
  }

  /**
   * Indica se o usuário deve ser tratado como externo na regra CDM + conta 10.
   * 1. Claim ehUsuarioInterno no token Ping (quando existir)
   * 2. internalUserIndicator retornado pelo GET /authorizations/{cpf} (Motor/CETIP)
   */
  isExplicitlyExternalUser(): boolean {
    this.syncTokenContentFromSession();
    const claim = this.getInternalUserClaimValue();

    if (claim !== undefined && claim !== null) {
      return !this.parseBooleanClaim(claim);
    }

    const fromAuthorizations = sessionStorage.getItem('INTERNAL_USER_INDICATOR');
    if (fromAuthorizations === null) {
      return false;
    }

    return fromAuthorizations !== 'true';
  }

  private getInternalUserClaimValue(): unknown {
    const accessClaims = this.decodeTokenClaims(sessionStorage.getItem('PING_ACCESS_TOKEN'));
    const idClaims = this.decodeTokenClaims(sessionStorage.getItem('PING_ID_TOKEN'));
    return this.resolveInternalUserClaim(accessClaims, idClaims);
  }

  private parseBooleanClaim(value: unknown): boolean {
    if (value === true || value === 'true') {
      return true;
    }

    if (value === 1 || value === '1' || value === 'S' || value === 's') {
      return true;
    }

    return false;
  }

  private decodeTokenClaims(token: string | null): Record<string, unknown> {
    if (!token) {
      return {};
    }

    try {
      return jwtDecode(token) as Record<string, unknown>;
    } catch {
      return {};
    }
  }

  private resolveInternalUserClaim(
    accessClaims: Record<string, unknown>,
    idClaims: Record<string, unknown>
  ): unknown {
    return accessClaims['ehUsuarioInterno']
      ?? accessClaims['usuarioInterno']
      ?? idClaims['ehUsuarioInterno']
      ?? idClaims['usuarioInterno'];
  }

  private buildTokenContent(accessToken: string | null, idToken: string | null): TokenContent {
    const accessClaims = this.decodeTokenClaims(accessToken);
    const idClaims = this.decodeTokenClaims(idToken);
    const claims = { ...idClaims, ...accessClaims };

    return Builder<TokenContent>()
      .ehUsuarioInterno(this.parseBooleanClaim(this.resolveInternalUserClaim(accessClaims, idClaims)))
      .exp(claims['exp'] as number)
      .familiaId(claims['familiaId'] as number)
      .nomeUsuario(claims['nomeUsuario'] as string)
      .participanteId(claims['participanteID'] as number)
      .perfilId(claims['perfilId'] as number)
      .sessao(claims['sessao'] as string)
      .usuarioId(claims['usuarioId'] as number)
      .build();
  }
}
